<template>
  <div class="markdown-editor">
    <div class="editor-toolbar">
      <el-button-group>
        <el-button type="text" icon="el-icon-bold" title="粗体" @click="insertMarkdown('**', '**')"></el-button>
        <el-button type="text" icon="el-icon-italic" title="斜体" @click="insertMarkdown('*', '*')"></el-button>
        <el-button type="text" icon="el-icon-link" title="链接" @click="insertMarkdown('[', '](https://example.com)')"></el-button>
        <el-button type="text" icon="el-icon-picture" title="图片" @click="insertMarkdown('![alt text](', ')')"></el-button>
      </el-button-group>
      <el-button-group>
        <el-button type="text" icon="el-icon-s-operation" title="标题" @click="insertMarkdown('## ', '')"></el-button>
        <el-button type="text" icon="el-icon-menu" title="列表" @click="insertMarkdown('- ', '')"></el-button>
        <el-button type="text" icon="el-icon-tickets" title="引用" @click="insertMarkdown('> ', '')"></el-button>
        <el-button type="text" icon="el-icon-s-grid" title="代码块" @click="insertMarkdown('```\n', '\n```')"></el-button>
      </el-button-group>
      <el-button type="text" icon="el-icon-view" title="预览" @click="togglePreview"></el-button>
    </div>
    <div v-if="!isPreview">
      <el-input
        type="textarea"
        v-model="content"
        :rows="rows"
        :placeholder="placeholder"
        ref="markdownEditor"
        @input="handleInput"
      ></el-input>
    </div>
    <div v-else class="markdown-preview markdown-body" v-html="renderedContent"></div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css'; // 引入代码高亮样式

export default {
  name: 'MarkdownEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    rows: {
      type: Number,
      default: 15
    },
    placeholder: {
      type: String,
      default: '请输入Markdown内容'
    }
  },
  data() {
    return {
      content: this.value,
      isPreview: false,
      md: null
    };
  },
  computed: {
    renderedContent() {
      return this.md ? this.md.render(this.content || '') : '';
    }
  },
  watch: {
    value(val) {
      this.content = val;
    }
  },
  created() {
    // 初始化markdown-it
    this.md = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: true,
      highlight: function(str, lang) {
        if (lang && hljs.getLanguage(lang)) {
          try {
            return hljs.highlight(lang, str).value;
          } catch (error) {
            console.error('Error highlighting code:', error);
          }
        }
        return ''; // 使用默认的转义
      }
    });
  },
  methods: {
    handleInput(val) {
      this.$emit('input', val);
    },
    insertMarkdown(before, after) {
      const textarea = this.$refs.markdownEditor.$el.querySelector('textarea');
      if (!textarea) return;
      
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selected = this.content.substring(start, end);
      
      this.content = this.content.substring(0, start) + before + selected + after + this.content.substring(end);
      
      this.$nextTick(() => {
        textarea.focus();
        textarea.setSelectionRange(start + before.length, start + before.length + selected.length);
      });
      
      this.$emit('input', this.content);
    },
    togglePreview() {
      this.isPreview = !this.isPreview;
    }
  }
};
</script>

<style scoped>
.markdown-editor {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.editor-toolbar {
  padding: 8px;
  border-bottom: 1px solid #dcdfe6;
  background-color: #f5f7fa;
}

.markdown-preview {
  min-height: 300px;
  padding: 15px;
  border: 1px solid #dcdfe6;
  border-radius: 0 0 4px 4px;
  background-color: #fff;
  overflow-y: auto;
}
</style>

<style>
/* GitHub风格的Markdown样式 */
.markdown-body {
  box-sizing: border-box;
  min-width: 200px;
  max-width: 980px;
  margin: 0 auto;
  padding: 45px;
}

@media (max-width: 767px) {
  .markdown-body {
    padding: 15px;
  }
}

.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
  margin-top: 24px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.25;
}

.markdown-body h1 {
  font-size: 2em;
  border-bottom: 1px solid #eaecef;
  padding-bottom: 0.3em;
}

.markdown-body h2 {
  font-size: 1.5em;
  border-bottom: 1px solid #eaecef;
  padding-bottom: 0.3em;
}

.markdown-body p {
  margin-top: 0;
  margin-bottom: 16px;
}

.markdown-body code {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  background-color: rgba(27,31,35,0.05);
  border-radius: 3px;
}

.markdown-body pre {
  padding: 16px;
  overflow: auto;
  font-size: 85%;
  line-height: 1.45;
  background-color: #f6f8fa;
  border-radius: 3px;
}

.markdown-body pre code {
  padding: 0;
  margin: 0;
  font-size: 100%;
  background-color: transparent;
  border: 0;
}

.markdown-body blockquote {
  padding: 0 1em;
  color: #6a737d;
  border-left: 0.25em solid #dfe2e5;
  margin: 0 0 16px 0;
}

.markdown-body ul,
.markdown-body ol {
  padding-left: 2em;
  margin-top: 0;
  margin-bottom: 16px;
}

.markdown-body img {
  max-width: 100%;
  box-sizing: content-box;
}

.markdown-body table {
  display: block;
  width: 100%;
  overflow: auto;
  border-spacing: 0;
  border-collapse: collapse;
}

.markdown-body table th,
.markdown-body table td {
  padding: 6px 13px;
  border: 1px solid #dfe2e5;
}

.markdown-body table tr {
  background-color: #fff;
  border-top: 1px solid #c6cbd1;
}

.markdown-body table tr:nth-child(2n) {
  background-color: #f6f8fa;
}
</style> 